import { useEffect, useState } from "react";
import { Copy, ExternalLink, Loader2 } from "lucide-react";
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
} from "@/components/ui/sheet";
import { fmtUSDprecise, timeAgo } from "@/lib/format";
import {
  abbreviateId,
  assetIdentityLabel,
  assetResolutionLabel,
  buildNarrativeSummary,
  buildTokenSummary,
  candidateKindLabel,
  candidateStatusLine,
  chainDisplayName,
  displayOr,
  entityInternalPath,
  explorerContractUrl,
  formatTimelineEvent,
  fmtPct01,
  fmtPoints,
  fmtScore,
  identityLabel,
  narrativeSignalLabel,
  narrativeStatusLabel,
  normalizeSeenWindow,
  opportunityExplanation,
  opportunityLabel,
  resolutionConfidenceLabel,
  resolutionMethodLabel,
} from "@/lib/hypeV2Format";
import {
  adminHypeV2NarrativeDetail,
  adminHypeV2TokenDetail,
  adminHypeV2CreateAlert,
} from "@/lib/api";

function Safe({ children }) {
  try {
    return children;
  } catch {
    return <div className="text-xs text-zinc-600">Unable to render field</div>;
  }
}

function SectionLabel({ children }) {
  return (
    <div className="mb-1.5 text-[10px] uppercase tracking-[0.14em] text-zinc-600">{children}</div>
  );
}

function Field({ label, value, empty, mono = true }) {
  return (
    <div className="flex justify-between gap-3 py-0.5">
      <span className="text-zinc-500">{label}</span>
      <span className={`text-right text-zinc-300 ${mono ? "font-mono text-[11px]" : "text-xs"}`}>
        {displayOr(value, empty || "Unavailable")}
      </span>
    </div>
  );
}

function StatusRow({ label, value, testId }) {
  if (!value) return null;
  return (
    <div className="flex justify-between gap-3 py-0.5" data-testid={testId}>
      <span className="text-zinc-500">{label}</span>
      <span className="text-right text-xs text-zinc-200">{value}</span>
    </div>
  );
}

function CopyableLink({ href, label, full, testId, tooltip }) {
  const [copied, setCopied] = useState(false);
  const copy = async () => {
    try {
      await navigator.clipboard.writeText(full || label || "");
      setCopied(true);
      setTimeout(() => setCopied(false), 1200);
    } catch {
      /* ignore */
    }
  };
  return (
    <div className="flex items-center justify-end gap-1.5">
      {href ? (
        <a
          href={href}
          target="_blank"
          rel="noopener noreferrer"
          title={tooltip || "Open in block explorer"}
          data-testid={testId}
          className="inline-flex items-center gap-1 font-mono text-[10px] text-zinc-300 underline-offset-2 hover:text-white hover:underline"
        >
          {abbreviateId(label)}
          <ExternalLink className="h-3 w-3 shrink-0 opacity-60" />
        </a>
      ) : (
        <span title={full || label} className="font-mono text-[10px] text-zinc-400">
          {abbreviateId(label) || "—"}
        </span>
      )}
      {(full || label) && (
        <button
          type="button"
          data-testid={`${testId || "copy"}-btn`}
          onClick={copy}
          className="rounded border border-white/10 p-0.5 text-zinc-500 hover:text-zinc-200"
          title={copied ? "Copied" : "Copy"}
        >
          <Copy className="h-3 w-3" />
        </button>
      )}
    </div>
  );
}

function Timeline({ items }) {
  if (!items?.length) {
    return (
      <div className="text-xs text-zinc-600" data-testid="hype-v2-timeline-empty">
        No timeline events yet.
      </div>
    );
  }
  return (
    <ol className="space-y-3" data-testid="hype-v2-timeline">
      {items.map((e, i) => {
        const f = formatTimelineEvent(e);
        return (
          <li key={`${e.timestamp}-${e.event_type}-${i}`} className="border-l border-white/10 pl-3">
            <div className="font-mono text-[10px] text-zinc-500">
              {e.timestamp ? timeAgo(e.timestamp) : "—"}
            </div>
            <div className="text-xs font-medium text-zinc-200">{f.title}</div>
            {f.detail && <div className="text-xs text-zinc-400">{f.detail}</div>}
            {f.confirm && (
              <div className="mt-0.5 text-[11px] text-zinc-500" data-testid="hype-v2-timeline-confirm">
                {f.confirm}
              </div>
            )}
            {f.meta && (
              <div className="mt-0.5 font-mono text-[9px] text-zinc-700">{f.meta}</div>
            )}
          </li>
        );
      })}
    </ol>
  );
}

function CandidateList({ candidates, audience }) {
  if (!candidates?.length) {
    return (
      <div className="text-xs text-zinc-600" data-testid="hype-v2-candidates-empty">
        No asset candidates identified yet.
      </div>
    );
  }
  return (
    <div className="space-y-2" data-testid="hype-v2-candidates">
      {candidates.map((c, i) => {
        const isNative = c.asset_kind === "NATIVE_ASSET";
        const addr = c.contract_address || c.mint_address;
        const explorer = !isNative && addr ? explorerContractUrl(c.chain, addr) : null;
        const statusLine = candidateStatusLine(c);
        const kind = candidateKindLabel(c);
        const resLabel = resolutionMethodLabel(c.resolution_method, { audience });
        const resConf = resolutionConfidenceLabel(c);
        return (
          <div
            key={`${c.external_id || c.native_id || c.name || i}`}
            className="space-y-1 rounded-xl border border-white/10 px-3 py-2"
            data-testid={`hype-v2-candidate-${i}`}
          >
            <div className="text-xs font-medium text-zinc-200" data-testid={`hype-v2-candidate-name-${i}`}>
              {c.name || "Unnamed candidate"}
              {c.symbol ? <span className="text-zinc-500"> ({c.symbol})</span> : null}
            </div>
            <div className="text-[11px] text-zinc-500" data-testid={`hype-v2-candidate-status-${i}`}>
              {statusLine}
              {kind && statusLine !== "Native Asset Candidate" ? ` · ${kind}` : ""}
            </div>
            {isNative && (c.status || c.candidate_status) === "ASSET_CANDIDATE" && (
              <div className="text-[11px] text-zinc-600">
                Matched to a canonical native asset; not yet linked to a BackOnFlip entity.
              </div>
            )}
            <Field label="Type" value={kind} empty="Unknown" />
            <Field label="Chain" value={chainDisplayName(c.chain)} empty="Unknown" />
            {isNative ? (
              <div className="flex justify-between gap-3 py-0.5">
                <span className="text-zinc-500">Canonical Asset ID</span>
                <CopyableLink
                  href={null}
                  label={c.native_id || `${c.chain}:${c.symbol}`}
                  full={c.native_id || `${c.chain}:${c.symbol}`}
                  testId={`candidate-native-${i}`}
                  tooltip="Copy canonical asset ID"
                />
              </div>
            ) : (
              <div className="flex justify-between gap-3 py-0.5">
                <span className="text-zinc-500">Contract Address</span>
                {addr ? (
                  <CopyableLink
                    href={explorer}
                    label={addr}
                    full={addr}
                    testId={`candidate-contract-${i}`}
                  />
                ) : (
                  <span className="font-mono text-[10px] text-zinc-600">Unavailable</span>
                )}
              </div>
            )}
            <Field label="Resolution" value={resLabel} empty="Unresolved" />
            <Field label="Resolution Confidence" value={resConf} empty="—" />
            <Field
              label="Status"
              value={identityLabel(c.status || c.candidate_status)}
              empty="—"
            />
          </div>
        );
      })}
    </div>
  );
}

function WhyScore({ breakdown }) {
  if (!breakdown?.length) return null;
  return (
    <section data-testid="hype-v2-why-score">
      <SectionLabel>Why this score?</SectionLabel>
      <div className="space-y-2">
        {breakdown.map((row) => (
          <div key={row.key} data-testid={`hype-v2-score-row-${row.key}`}>
            <div className="text-xs text-zinc-300">{row.label}</div>
            <div className="font-mono text-[11px] text-zinc-500">
              {fmtPct01(row.component) ?? "0.00"} × {row.weight_pct ?? "—"}% = {fmtPoints(row.points)} pts
            </div>
          </div>
        ))}
      </div>
      <div className="mt-2 text-[10px] text-zinc-600" data-testid="hype-v2-score-footnote">
        Hype Score weights remain 35 / 30 / 20 / 15. This is not a return prediction.
      </div>
    </section>
  );
}

export default function HypeDetailDrawer({
  open,
  onOpenChange,
  kind,
  id,
  audience = "internal",
}) {
  const [state, setState] = useState({ loading: false, error: null, data: null });
  const [watchState, setWatchState] = useState("idle"); // idle | watching | error

  useEffect(() => {
    if (!open || !id || !kind) {
      setState({ loading: false, error: null, data: null });
      setWatchState("idle");
      return;
    }
    let cancelled = false;
    setState({ loading: true, error: null, data: null });
    setWatchState("idle");
    const run = Promise.resolve(
      kind === "narrative"
        ? adminHypeV2NarrativeDetail(id, { audience })
        : adminHypeV2TokenDetail(id, { audience }),
    );
    run.then(
      (data) => {
        if (!cancelled) setState({ loading: false, error: null, data });
      },
      () => {
        if (!cancelled) setState({ loading: false, error: "Failed to load detail", data: null });
      },
    );
    return () => {
      cancelled = true;
    };
  }, [open, kind, id, audience]);

  const data = state.data;
  const showProv = audience === "internal";
  const title =
    kind === "narrative"
      ? data?.title || data?.canonical_narrative_key || "Narrative"
      : data?.symbol || data?.name || "Token";
  const ei = data?.external_intelligence || {};
  const assetId = data?.asset_identity;
  const narrIntel = data?.narrative_intelligence_status || data?.narrative_intelligence;
  const oppLabel = opportunityLabel(data?.opportunity, data);
  const oppExplain = opportunityExplanation(
    oppLabel === "Insufficient Data" && data?.opportunity?.status === "STALE"
      ? { ...data, opportunity: { ...data.opportunity, status: "INSUFFICIENT_DATA", reason: "no_narrative_signals" } }
      : data,
  );
  const seen = normalizeSeenWindow(data?.first_detected_at, data?.last_seen_at);
  const contract = data?.contract_address || data?.mint_address;
  const explorer = explorerContractUrl(data?.chain, contract);
  const entityHref = entityInternalPath(data?.slug);
  const summary =
    kind === "narrative" ? buildNarrativeSummary(data) : buildTokenSummary(data);
  const narrSignal =
    kind === "narrative"
      ? narrativeSignalLabel(data)
      : narrativeStatusLabel(narrIntel, data);
  const assetIdentityDisplay =
    kind === "token" ? assetIdentityLabel(assetId, data) : null;
  const assetResolution = kind === "narrative" ? assetResolutionLabel(data) : null;

  const watch = async () => {
    try {
      await adminHypeV2CreateAlert({
        name: `Watch ${title}`,
        narrative_key: kind === "narrative" ? data?.canonical_narrative_key || id : undefined,
        entity_id: kind === "token" ? data?.entity_id : undefined,
        channels: ["in_app"],
      });
      setWatchState("watching");
    } catch {
      setWatchState("error");
    }
  };

  const narrMomentum = fmtPct01(
    kind === "narrative"
      ? data?.narrative_momentum ?? data?.momentum
      : ei.narrative_momentum ?? ei.experimental?.narrative_momentum,
  );
  const narrConfidence = fmtPct01(
    kind === "narrative"
      ? data?.signal_confidence ?? data?.provider_consensus ?? data?.consensus_score
      : ei.signal_confidence ?? ei.provider_consensus ?? ei.consensus_score,
  );
  const sourceCount =
    kind === "narrative"
      ? data?.provider_count ?? data?.source_count
      : ei.source_count ?? ei.providers?.length ?? ei.experimental?.provider_count;
  const consensus = fmtPct01(
    kind === "narrative"
      ? data?.provider_consensus ?? data?.consensus_score
      : ei.provider_consensus ?? ei.consensus_score,
  );

  const backingTotal = data?.backing?.total_backed_usd;
  const backingVel = fmtPct01(data?.backing?.backing_velocity ?? data?.components?.backing_velocity);
  const callerAct = fmtPct01(
    data?.caller_activity?.component ?? data?.components?.caller_activity,
  );

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent
        side="right"
        className="w-full overflow-y-auto border-white/10 bg-[#080808] text-zinc-200 sm:max-w-md"
        data-testid="hype-v2-drawer"
      >
        <SheetHeader className="text-left">
          <SheetTitle className="text-zinc-100">{title}</SheetTitle>
          {summary ? (
            <SheetDescription
              className="text-sm leading-relaxed text-zinc-400"
              data-testid="hype-v2-summary"
            >
              {summary}
            </SheetDescription>
          ) : (
            <SheetDescription className="sr-only">Hype Radar detail</SheetDescription>
          )}
        </SheetHeader>

        {state.loading && (
          <div className="flex justify-center py-12" data-testid="hype-v2-drawer-loading">
            <Loader2 className="h-5 w-5 animate-spin text-zinc-500" />
          </div>
        )}
        {state.error && (
          <div className="mt-4 text-sm text-negative" data-testid="hype-v2-drawer-error">
            {state.error}
          </div>
        )}
        {!state.loading && !state.error && !data && open && id && (
          <div className="mt-4 text-sm text-zinc-600" data-testid="hype-v2-drawer-empty">
            No detail available yet.
          </div>
        )}

        {data && (
          <div className="mt-4 space-y-5 text-xs">
            {/* Status hierarchy — each state once */}
            <Safe>
              <section
                className="space-y-0.5 rounded-xl border border-white/10 px-3 py-2"
                data-testid="hype-v2-status-panel"
              >
                {kind === "token" && (
                  <StatusRow
                    label="Asset Identity"
                    value={assetIdentityDisplay}
                    testId="hype-v2-asset-identity"
                  />
                )}
                <StatusRow
                  label="Narrative Intelligence"
                  value={narrSignal}
                  testId="hype-v2-narrative-status"
                />
                {kind === "narrative" && (
                  <StatusRow
                    label="Asset Resolution"
                    value={assetResolution}
                    testId="hype-v2-asset-resolution"
                  />
                )}
                <StatusRow
                  label="Opportunity"
                  value={oppLabel}
                  testId="hype-v2-opportunity"
                />
                {oppExplain && (
                  <p
                    className="mt-1.5 text-[11px] leading-relaxed text-zinc-500"
                    data-testid="hype-v2-opportunity-explain"
                  >
                    {oppExplain}
                  </p>
                )}
              </section>
            </Safe>

            {kind === "token" && (
              <>
                <section data-testid="hype-v2-token-score">
                  <SectionLabel>Hype Score</SectionLabel>
                  <div className="font-mono text-2xl text-gold">{fmtScore(data.hype_score)}</div>
                </section>

                <WhyScore breakdown={data.score_breakdown} />

                <section data-testid="hype-v2-token-narrative">
                  <SectionLabel>Narrative Intelligence</SectionLabel>
                  <Field label="Narrative Momentum" value={narrMomentum} empty="No narrative signal" />
                  <Field label="Narrative Confidence" value={narrConfidence} empty="No confidence data" />
                  <Field
                    label="Independent Sources"
                    value={sourceCount == null || Number(sourceCount) === 0 ? null : sourceCount}
                    empty="No sources detected"
                  />
                  <Field label="Provider Consensus" value={consensus} empty="No consensus data" />
                </section>

                <section data-testid="hype-v2-token-market">
                  <SectionLabel>Market</SectionLabel>
                  <Field
                    label="Price"
                    value={data.market?.price_usd == null ? null : fmtUSDprecise(data.market.price_usd)}
                    empty="Price unavailable"
                  />
                  <Field
                    label="Volume 24h"
                    value={
                      data.market?.volume_24h_usd == null
                        ? null
                        : fmtUSDprecise(data.market.volume_24h_usd)
                    }
                    empty="Volume unavailable"
                  />
                  <Field
                    label="Liquidity"
                    value={
                      data.market?.liquidity_usd == null
                        ? null
                        : fmtUSDprecise(data.market.liquidity_usd)
                    }
                    empty="Liquidity unavailable"
                  />
                  <Field
                    label="Market Cap"
                    value={
                      data.market?.market_cap_usd == null
                        ? null
                        : fmtUSDprecise(data.market.market_cap_usd)
                    }
                    empty="Market cap unavailable"
                  />
                </section>

                <section data-testid="hype-v2-token-backing">
                  <SectionLabel>Conviction & Caller Activity</SectionLabel>
                  <Field
                    label="Total backed"
                    value={backingTotal == null || Number(backingTotal) === 0 ? null : fmtUSDprecise(backingTotal)}
                    empty="No verified backing yet"
                  />
                  <Field label="Backing velocity" value={backingVel} empty="0.00" />
                  <Field
                    label="Caller activity"
                    value={callerAct && Number(callerAct) > 0 ? callerAct : null}
                    empty="No caller activity detected"
                  />
                </section>

                <section data-testid="hype-v2-token-identity">
                  <SectionLabel>Asset details</SectionLabel>
                  <Field label="Symbol" value={data.symbol} empty="Unknown" />
                  <Field label="Name" value={data.name} empty="Unknown" />
                  <Field label="Chain" value={chainDisplayName(data.chain)} empty="Unknown" />
                  <div className="flex justify-between gap-3 py-0.5">
                    <span className="text-zinc-500">Contract Address</span>
                    <CopyableLink
                      href={explorer}
                      label={contract}
                      full={contract}
                      testId="hype-v2-contract-link"
                    />
                  </div>
                  <div className="flex justify-between gap-3 py-0.5">
                    <span className="text-zinc-500">Entity</span>
                    {entityHref ? (
                      <a
                        href={entityHref}
                        data-testid="hype-v2-entity-link"
                        className="font-mono text-[10px] text-zinc-300 underline-offset-2 hover:text-white hover:underline"
                      >
                        {abbreviateId(data.entity_id)}
                      </a>
                    ) : (
                      <CopyableLink
                        href={null}
                        label={data.entity_id}
                        full={data.entity_id}
                        testId="hype-v2-entity-copy"
                      />
                    )}
                  </div>
                </section>
              </>
            )}

            {kind === "narrative" && (
              <section data-testid="hype-v2-narrative-fields">
                <SectionLabel>Narrative Intelligence</SectionLabel>
                <Field label="Narrative Momentum" value={narrMomentum} empty="No narrative signal" />
                <Field label="Narrative Confidence" value={narrConfidence} empty="No confidence data" />
                <Field
                  label="Independent Sources"
                  value={sourceCount == null || Number(sourceCount) === 0 ? null : sourceCount}
                  empty="No sources detected"
                />
                <Field label="Provider Consensus" value={consensus} empty="No consensus data" />
                <Field
                  label="First seen"
                  value={seen.first ? timeAgo(seen.first) : null}
                  empty="Unknown"
                />
                <Field
                  label="Last seen"
                  value={seen.last ? timeAgo(seen.last) : null}
                  empty="Unknown"
                />
                <span
                  className="hidden"
                  data-testid="hype-v2-seen-iso"
                  data-first={seen.first || ""}
                  data-last={seen.last || ""}
                />
              </section>
            )}

            <section>
              <SectionLabel>
                {kind === "narrative" ? "Related asset(s)" : "Asset Candidates"}
              </SectionLabel>
              <CandidateList candidates={data.asset_candidates} audience={audience} />
            </section>

            <section>
              <SectionLabel>Timeline</SectionLabel>
              <Timeline items={data.timeline} />
            </section>

            <section data-testid="hype-v2-watch-section">
              <button
                type="button"
                data-testid="hype-v2-watch"
                onClick={watch}
                disabled={watchState === "watching"}
                title="Track this asset or narrative and surface meaningful changes in narrative momentum, backing, caller activity, or market velocity. Stores an in-app watchlist rule only — email/Telegram alerts are not sent."
                className="rounded-full border border-white/15 px-3 py-1.5 text-xs hover:bg-white/5 disabled:opacity-70"
              >
                {watchState === "watching" ? "Watching" : "Add to Watchlist"}
              </button>
              <p className="mt-1.5 text-[10px] leading-relaxed text-zinc-600">
                Track meaningful changes in narrative momentum, backing, caller activity, or market
                velocity. Saves an in-app watchlist rule only.
              </p>
              {watchState === "watching" && (
                <p className="mt-1 text-[11px] text-zinc-400" data-testid="hype-v2-watch-confirm">
                  Added to your in-app watchlist.
                </p>
              )}
              {watchState === "error" && (
                <p className="mt-1 text-[11px] text-negative" data-testid="hype-v2-watch-error">
                  Could not add to watchlist.
                </p>
              )}
            </section>

            {showProv && ((data.providers || []).length > 0 || data.diagnostics) && (
              <details className="rounded-xl border border-white/5 px-3 py-2" data-testid="hype-v2-internal-provenance">
                <summary className="cursor-pointer text-[10px] uppercase tracking-[0.14em] text-zinc-700">
                  Internal provenance
                </summary>
                <div className="mt-2 flex flex-wrap gap-1">
                  {(data.providers || []).map((p) => (
                    <span
                      key={p}
                      className="rounded border border-white/10 px-1.5 py-0.5 font-mono text-[9px] uppercase text-zinc-600"
                    >
                      {p}
                    </span>
                  ))}
                </div>
                {(data.asset_candidates || []).map((c, i) =>
                  c.resolution_method || c.provider || c.external_id ? (
                    <div
                      key={`prov-${i}`}
                      className="mt-1 font-mono text-[9px] text-zinc-700"
                      data-testid={`hype-v2-diag-candidate-${i}`}
                    >
                      {c.provider || "—"} · {c.resolution_method || "—"} ·{" "}
                      {c.external_id || c.native_id || "—"}
                    </div>
                  ) : null,
                )}
              </details>
            )}
          </div>
        )}
      </SheetContent>
    </Sheet>
  );
}
